import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import AppLoader from './components/AppLoader';
import PageSkeleton from './components/PageSkeleton';
import './App.css';

// ─── Skeleton loader shown during code-split chunk downloads ────
const PageLoader = () => (
  <div className="page-loader" role="status" aria-label="Loading page">
    <div className="page-loader__bar" />
  </div>
);

// ─── Lazy-loaded pages (code splitting) ─────────────────────────
// Home is the most critical page — use webpackPrefetch to start loading it
// immediately after the main bundle, before user interaction.
const Home = lazy(() => import(/* webpackPrefetch: true */ './pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Industries = lazy(() => import('./pages/Industries'));
const IndustryDetail = lazy(() => import('./pages/IndustryDetail'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const CaseStudyDetail = lazy(() => import('./pages/CaseStudyDetail'));
const Careers = lazy(() => import('./pages/Careers'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const Login = lazy(() => import('./pages/Login'));

// Admin pages — only loaded when visiting /admin/*
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminProjects = lazy(() => import('./pages/admin/AdminProjects'));
const AdminBlogs = lazy(() => import('./pages/admin/AdminBlogs'));
const AdminCaseStudies = lazy(() => import('./pages/admin/AdminCaseStudies'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminServiceNew = lazy(() => import('./pages/admin/AdminServiceNew'));
const AdminServiceEdit = lazy(() => import('./pages/admin/AdminServiceEdit'));
const AdminTestimonials = lazy(() => import('./pages/admin/AdminTestimonials'));

/**
 * Wrapper that blocks URL access to hidden pages.
 * Maps each navLink key to its route path prefix so that both
 * parent routes (/services) and child routes (/services/:slug) are blocked.
 */
const ProtectedRoute = ({ basePath, children }) => {
  const { loaded, hiddenPaths } = useSiteSettings();

  // While settings are loading, render nothing to avoid flash
  if (!loaded) return null;

  // If the base path is in the hidden set, redirect to home
  if (hiddenPaths.has(basePath)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PublicLayout = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const { loaded } = useSiteSettings();

  // Show full-screen loader while settings are being fetched
  if (!loaded) return <AppLoader />;

  return (
    <>
      {!isLoginPage && <Navbar />}
      <main className="main">
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={
              <ProtectedRoute basePath="/services"><Services /></ProtectedRoute>
            } />
            <Route path="/services/:slug" element={
              <ProtectedRoute basePath="/services"><ServiceDetail /></ProtectedRoute>
            } />
            <Route path="/industries" element={
              <ProtectedRoute basePath="/industries"><Industries /></ProtectedRoute>
            } />
            <Route path="/industries/:slug" element={
              <ProtectedRoute basePath="/industries"><IndustryDetail /></ProtectedRoute>
            } />
            <Route path="/portfolio" element={
              <ProtectedRoute basePath="/portfolio"><Portfolio /></ProtectedRoute>
            } />
            <Route path="/portfolio/:slug" element={
              <ProtectedRoute basePath="/portfolio"><ProjectDetail /></ProtectedRoute>
            } />
            <Route path="/blog" element={
              <ProtectedRoute basePath="/blog"><Blog /></ProtectedRoute>
            } />
            <Route path="/blog/:slug" element={
              <ProtectedRoute basePath="/blog"><BlogDetail /></ProtectedRoute>
            } />
            <Route path="/about" element={
              <ProtectedRoute basePath="/about"><About /></ProtectedRoute>
            } />
            <Route path="/contact" element={
              <ProtectedRoute basePath="/contact"><Contact /></ProtectedRoute>
            } />
            <Route path="/case-studies" element={
              <ProtectedRoute basePath="/case-studies"><CaseStudies /></ProtectedRoute>
            } />
            <Route path="/case-studies/:slug" element={
              <ProtectedRoute basePath="/case-studies"><CaseStudyDetail /></ProtectedRoute>
            } />
            <Route path="/careers" element={
              <ProtectedRoute basePath="/careers"><Careers /></ProtectedRoute>
            } />
            <Route path="/careers/:id" element={
              <ProtectedRoute basePath="/careers"><JobDetail /></ProtectedRoute>
            } />
            <Route path="/login" element={<Login />} />
            {/* Catch-all: redirect unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      {!isLoginPage && <Footer />}
    </>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  if (isAdmin) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="services/new" element={<AdminServiceNew />} />
            <Route path="services/edit/:id" element={<AdminServiceEdit />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="case-studies" element={<AdminCaseStudies />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  return <PublicLayout />;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <SiteSettingsProvider>
            <div className="app">
              <AppRoutes />
            </div>
          </SiteSettingsProvider>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
